import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { meResponseSchema, type MeResponse } from '@shared/api';
import { api, HTTPError } from '@web/lib/api';
import { keys } from '@web/lib/keys';

export type SessionUser = MeResponse['user'];

/**
 * Who is signed in.
 *
 * A 401 here is not an error — it is the ordinary state of a tab whose session
 * expired overnight — so it resolves to `null` and the router sends the
 * operator to the login screen. Treating it as an error would put a red box on
 * the login page every time someone opens the app signed out.
 */
async function fetchSession(): Promise<SessionUser | null> {
  try {
    const response = await api.get('/api/me', meResponseSchema);
    return response.user;
  } catch (error) {
    if (error instanceof HTTPError && error.status === 401) return null;
    throw error;
  }
}

export function useSession(): UseQueryResult<SessionUser | null> {
  return useQuery({
    queryKey: keys.me,
    queryFn: fetchSession,
    staleTime: 60_000,
    retry: false,
  });
}
